import { FILE_TYPE_ICONS } from '@/components_v2/AttachmentItem/AttachmentItem';
import { MediaType } from '@/hooks/Feedback/interfaces';

export type CommentMediaKind = 'image' | 'video' | 'audio' | 'pdf' | 'other';

const IMAGE_EXTS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'heic',
  'heif',
  'svg',
];
const VIDEO_EXTS = ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v', '3gp'];
const AUDIO_EXTS = ['mp3', 'wav', 'm4a', 'opus'];
const EXCEL_EXTS = ['xlsx', 'xls', 'csv', 'ods'];
const WORD_EXTS = ['docx', 'doc', 'pages', 'odt', 'rtf'];
const POWERPOINT_EXTS = ['pptx', 'ppt', 'odp'];

export type UploadCategory = 'image' | 'video' | 'audio' | 'document';

const MB = 1024 * 1024; // base binária

// Limites da WhatsApp Business API (nossa referência) — mesma validação
// aplicada pela API. Mantém sincronizado com o backend.
export const UPLOAD_CATEGORIES: Record<
  UploadCategory,
  { extensions: string[]; maxBytes: number }
> = {
  image: {
    extensions: ['jpeg', 'jpg', 'png', 'gif', 'webp'],
    maxBytes: 5 * MB,
  },
  video: {
    extensions: ['mp4', 'mkv', 'mov'],
    maxBytes: 40 * MB,
  },
  audio: {
    extensions: ['mp3', 'wav', 'm4a', 'opus'],
    maxBytes: 16 * MB,
  },
  document: {
    extensions: [
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
      'csv',
      'ods',
      'ppt',
      'pptx',
      'odp',
    ],
    maxBytes: 100 * MB,
  },
};

// Derivada da tabela acima — não duplicar a lista.
export const ALLOWED_UPLOAD_EXTENSIONS = Array.from(
  new Set(Object.values(UPLOAD_CATEGORIES).flatMap((c) => c.extensions)),
);

export function getFileExtension(nameOrUrl?: string): string {
  if (!nameOrUrl) return '';
  const withoutQuery = nameOrUrl.split('?')[0];
  return withoutQuery.split('.').pop()?.toLowerCase() ?? '';
}

export function getUploadCategory(fileName: string): UploadCategory | null {
  const ext = getFileExtension(fileName);
  const entry = (Object.entries(UPLOAD_CATEGORIES) as [
    UploadCategory,
    typeof UPLOAD_CATEGORIES[UploadCategory],
  ][]).find(([, { extensions }]) => extensions.includes(ext));
  return entry ? entry[0] : null;
}

export function isAllowedFileExtension(fileName: string): boolean {
  return getUploadCategory(fileName) !== null;
}

export type UploadValidation =
  | { ok: true }
  | { ok: false; reason: 'type' }
  | { ok: false; reason: 'empty' }
  | { ok: false; reason: 'size'; category: UploadCategory; maxBytes: number };

export function validateUploadFile(file: File): UploadValidation {
  const category = getUploadCategory(file.name);
  if (!category) return { ok: false, reason: 'type' };
  if (file.size === 0) return { ok: false, reason: 'empty' };
  const { maxBytes } = UPLOAD_CATEGORIES[category];
  if (file.size > maxBytes)
    return { ok: false, reason: 'size', category, maxBytes };
  return { ok: true };
}

export function getMediaKind(
  nameOrUrl?: string,
  serverMediaType?: MediaType,
): CommentMediaKind {
  if (serverMediaType === 'image') return 'image';
  if (serverMediaType === 'video') return 'video';
  if (serverMediaType === 'audio') return 'audio';

  if (!nameOrUrl) return 'other';
  const ext = getFileExtension(nameOrUrl);
  if (IMAGE_EXTS.includes(ext)) return 'image';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  if (AUDIO_EXTS.includes(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  return 'other';
}

export function getFileChipConfig(
  kind: CommentMediaKind,
  fileName: string,
): { icon: React.ElementType; bg: string } {
  if (kind === 'pdf') return { icon: FILE_TYPE_ICONS.pdf, bg: 'red.500' };
  if (kind === 'audio')
    return { icon: FILE_TYPE_ICONS.audio, bg: 'purple.500' };
  const ext = (fileName || '').split('.').pop()?.toLowerCase() ?? '';
  if (EXCEL_EXTS.includes(ext))
    return { icon: FILE_TYPE_ICONS.excel, bg: 'green.600' };
  if (WORD_EXTS.includes(ext))
    return { icon: FILE_TYPE_ICONS.word, bg: 'blue.600' };
  if (POWERPOINT_EXTS.includes(ext))
    return { icon: FILE_TYPE_ICONS.powerpoint, bg: 'orange.500' };
  return { icon: FILE_TYPE_ICONS.default, bg: 'gray.500' };
}
